import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "./api";
import {
  DEMO_STAFF_STORAGE_EVENT,
  DEMO_STAFF_STORAGE_KEY,
  accessView,
  demoStaffPreferenceFromStoredValue,
  demoStaffPreferenceWrite,
} from "../domain/access";
import { isClerkEnabled } from "./clerkEnv";

const readDemoStaffPreference = (): boolean => {
  return demoStaffPreferenceFromStoredValue({
    clerkEnabled: isClerkEnabled,
    storedValue: window.localStorage.getItem(DEMO_STAFF_STORAGE_KEY),
  });
};

export const setDemoStaffPreference = (enabled: boolean) => {
  const write = demoStaffPreferenceWrite({ clerkEnabled: isClerkEnabled, enabled });
  switch (write.kind) {
    case "noop":
      return;
    case "set":
      window.localStorage.setItem(write.key, write.value);
      break;
    case "remove":
      window.localStorage.removeItem(write.key);
      break;
  }
  window.dispatchEvent(new Event(write.eventName));
};

/**
 * Current caller's access level. In dev mode (no Clerk) everything is open so
 * local development isn't blocked. With Clerk, staff status comes from the
 * synced user row's role, with a demo preference to bridge the hosted mock
 * site's staff/client switch immediately after a role mutation succeeds.
 */
export function useAccess(): { isDev: boolean; isStaff: boolean; canWrite: boolean; isInternalAdmin: boolean } {
  const isDev = !isClerkEnabled;
  const current = useQuery(api.users.current, isDev ? "skip" : {});
  const [demoStaff, setDemoStaff] = useState(readDemoStaffPreference);

  useEffect(() => {
    if (!isClerkEnabled) return;
    const refresh = () => setDemoStaff(readDemoStaffPreference());
    window.addEventListener(DEMO_STAFF_STORAGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(DEMO_STAFF_STORAGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return { ...accessView({ clerkEnabled: isClerkEnabled, demoStaff, role: current?.role }),
    isInternalAdmin: isDev || (current?.active !== false && current?.role === "internalAdmin"),
    canWrite: isDev || (current !== undefined && current !== null && current.active !== false && current.role !== "clientReadOnly") };
}
